/**
 * Mock reminder service — backed by the in-memory `mockReminders` fixtures.
 *
 * Returns copies and simulates latency. Like the documents mock, it never
 * mutates the shared fixture array — the tab owns React state, so `update`
 * returns a synthesised updated reminder for the caller to merge.
 */

import { mockReminders } from '@/data/mockReminders'
import { mockDelay } from '@/services/factory'
import type { ReminderService } from './reminders.service'
import type {
  Reminder,
  CreateReminderInput,
  UpdateReminderInput,
  ReminderEntityType,
} from '@/types/reminder.types'

function findOrThrow(id: string): Reminder {
  const r = mockReminders.find((x) => x.id === id)
  if (!r) throw new Error('Reminder not found')
  return r
}

export function createMockReminderService(): ReminderService {
  return {
    async listForEntity(
      _entityType: ReminderEntityType,
      entityId: string
    ): Promise<Reminder[]> {
      await mockDelay(300)
      return mockReminders
        .filter((r) => r.entityId === entityId)
        .map((r) => ({ ...r }))
    },

    async create(input: CreateReminderInput): Promise<Reminder> {
      await mockDelay(400)
      const now = new Date().toISOString()
      return {
        id: `rem-${Date.now()}`,
        entityType: input.entityType,
        entityId: input.entityId,
        title: input.title,
        description: input.description,
        reminderType: input.reminderType ?? 'custom',
        priority: input.priority ?? 'medium',
        dueDate: input.dueDate,
        status: 'pending',
        notifyChannels: input.notifyChannels ?? ['in_app'],
        assignedToUserId: input.assignedToUserId,
        assignedToName: input.assignedToName,
        recurrence: input.recurrence ?? 'none',
        remindBeforeMinutes: input.remindBeforeMinutes,
        isOverdue: new Date(input.dueDate).getTime() < Date.now(),
        createdByName: 'You',
        createdAt: now,
        updatedAt: now,
      }
    },

    async update(id: string, patch: UpdateReminderInput): Promise<Reminder> {
      await mockDelay(300)
      const current = findOrThrow(id)
      const now = new Date().toISOString()

      if (patch.snoozeMinutes != null) {
        return {
          ...current,
          status: 'snoozed',
          snoozedUntil: new Date(Date.now() + patch.snoozeMinutes * 60_000).toISOString(),
          snoozeCount: (current.snoozeCount ?? 0) + 1,
          updatedAt: now,
        }
      }
      if (patch.status === 'completed') {
        return {
          ...current,
          status: 'completed',
          completedAt: now,
          completedByName: 'You',
          completionNotes: patch.notes,
          updatedAt: now,
        }
      }
      if (patch.status === 'dismissed') {
        return {
          ...current,
          status: 'dismissed',
          dismissedAt: now,
          dismissedByName: 'You',
          dismissedReason: patch.notes,
          updatedAt: now,
        }
      }

      return {
        ...current,
        title: patch.title ?? current.title,
        description: patch.description ?? current.description,
        priority: patch.priority ?? current.priority,
        dueDate: patch.dueDate ?? current.dueDate,
        status: patch.status ?? current.status,
        notifyChannels: patch.notifyChannels ?? current.notifyChannels,
        updatedAt: now,
      }
    },

    async remove(_id: string): Promise<void> {
      await mockDelay(300)
    },
  }
}
